import { useEffect, useMemo, useState } from "react";
import { api, productImage, TYPE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Sparkles, ChevronLeft, ChevronRight, ClipboardList, Check, ListChecks, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";

const BATCH = 20;

const copy = (text, label) => {
  navigator.clipboard.writeText(text || "");
  toast.success(`${label} copied`);
};

const CopyBtn = ({ text, label, tid }) => (
  <button onClick={() => copy(text, label)} data-testid={tid}
    className="h-7 px-2 rounded text-[11px] font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-600 border border-zinc-200 flex items-center gap-1 transition-colors shrink-0">
    <Copy className="w-3 h-3" /> Copy
  </button>
);

export default function RangeMe() {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [ptype, setPtype] = useState(null);
  const [view, setView] = useState("sheet"); // "sheet" | "checklist"
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [stateProduced, setStateProduced] = useState(localStorage.getItem("honorifica_rangeme_state") || "");
  const [reimported, setReimported] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("honorifica_rangeme_reimported") || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => { localStorage.setItem("honorifica_rangeme_state", stateProduced); }, [stateProduced]);

  const persistReimported = (next) => {
    setReimported(next);
    localStorage.setItem("honorifica_rangeme_reimported", JSON.stringify([...next]));
  };
  const toggleReimported = (id) => {
    const next = new Set(reimported);
    next.has(id) ? next.delete(id) : next.add(id);
    persistReimported(next);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products?workflow_state=published");
        const c = {};
        data.forEach((p) => { c[p.product_type] = (c[p.product_type] || 0) + 1; });
        setCounts(c);
        const first = Object.keys(c).sort((a, b) => c[b] - c[a])[0];
        if (first) setPtype(first);
      } catch { toast.error("Could not load products"); }
      finally { setLoading(false); }
    })();
  }, []);

  const types = useMemo(
    () => Object.keys(counts).sort((a, b) => counts[b] - counts[a]),
    [counts]
  );

  const genSheet = async (typeArg = ptype, skipArg = 0) => {
    if (!typeArg) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rangeme/batch-sheet", { product_type: typeArg, skip: skipArg, limit: BATCH });
      setSheet(data);
      setSkip(data.skip);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build cheat sheet"); }
    finally { setBusy(false); }
  };

  const loadChecklist = async (typeArg = ptype) => {
    if (!typeArg) return;
    setBusy(true);
    try {
      const { data } = await api.get("/rangeme/reimport-checklist", { params: { product_type: typeArg } });
      setChecklist(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load checklist"); }
    finally { setBusy(false); }
  };

  const pickType = (t) => {
    setPtype(t); setSheet(null); setChecklist(null); setSkip(0);
  };

  const switchView = (v) => { setView(v); };

  const total = sheet?.total || 0;
  const batchCount = Math.max(1, Math.ceil(total / BATCH));
  const batchIndex = Math.floor((sheet?.skip || 0) / BATCH);

  const defaultsText = useMemo(() => {
    if (!sheet) return "";
    const rows = [`Category: ${sheet.category_suggestion}`];
    if (stateProduced.trim()) rows.push(`State Produced: ${stateProduced.trim()}`);
    Object.entries(sheet.common_defaults || {}).forEach(([k, v]) => rows.push(`${k}: ${v}`));
    return rows.join("\n");
  }, [sheet, stateProduced]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C05A45]" /></div>;

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="rangeme-page">
      <div className="mb-5">
        <p className="overline text-[#C05A45]">Wholesale · RangeMe import helper</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">RangeMe Batch Cheat Sheet</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          RangeMe imports products from Shopify <strong>20 at a time</strong> and can't auto-fill its own fields
          (category, USPs, distribution). Import <strong>one product type per batch</strong> so the shared
          "Common Product Details" apply cleanly, then copy each product's short name and bullets below.
        </p>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 mb-4 bg-zinc-100 rounded-lg p-1 w-fit" data-testid="view-toggle">
        {[["sheet", "Batch Cheat Sheet", Sparkles], ["checklist", "Re-Import Checklist", ListChecks]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => switchView(k)} data-testid={`view-${k}`}
            className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === k ? "bg-white text-[#C05A45] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Type selector */}
      <div className="bg-white border border-zinc-200 rounded-lg p-3 mb-4" data-testid="type-selector">
        <div className="text-xs font-semibold text-zinc-500 mb-2">Choose a product type</div>
        <div className="flex flex-wrap gap-2">
          {types.map((t) => (
            <button key={t} onClick={() => pickType(t)} data-testid={`type-${t}`}
              className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${ptype === t ? "bg-[#C05A45] border-[#C05A45] text-white" : "bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>
              {TYPE_LABELS[t] || t} <span className={ptype === t ? "text-white/70" : "text-zinc-400"}>· {counts[t]}</span>
            </button>
          ))}
          {types.length === 0 && <span className="text-xs text-zinc-400">No published products found.</span>}
        </div>
        {ptype && view === "sheet" && (
          <button onClick={() => genSheet(ptype, 0)} disabled={busy} data-testid="generate-sheet-btn"
            className="mt-3 h-9 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Building…" : `Build cheat sheet for ${TYPE_LABELS[ptype] || ptype}`}
          </button>
        )}
        {ptype && view === "checklist" && (
          <button onClick={() => loadChecklist(ptype)} disabled={busy} data-testid="load-checklist-btn"
            className="mt-3 h-9 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
            {busy ? "Loading…" : `Show ${TYPE_LABELS[ptype] || ptype} re-import checklist`}
          </button>
        )}
      </div>

      {/* ---- CHEAT SHEET VIEW ---- */}
      {view === "sheet" && sheet && (
        <>
          <div className="flex items-center justify-between mb-3" data-testid="batch-nav">
            <div className="text-sm text-zinc-600">
              <strong>{TYPE_LABELS[sheet.product_type] || sheet.product_type}</strong> · batch{" "}
              <strong>{batchIndex + 1}</strong> of {batchCount}
              <span className="text-zinc-400"> ({total} total, {BATCH}/import)</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => genSheet(ptype, Math.max(0, skip - BATCH))} disabled={busy || batchIndex === 0}
                data-testid="prev-batch-btn"
                className="h-8 px-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600 text-xs flex items-center gap-1 disabled:opacity-40 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button onClick={() => genSheet(ptype, skip + BATCH)} disabled={busy || batchIndex + 1 >= batchCount}
                data-testid="next-batch-btn"
                className="h-8 px-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600 text-xs flex items-center gap-1 disabled:opacity-40 transition-colors">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="common-defaults-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#C05A45]" />
                <h2 className="font-display text-lg font-bold">Common Product Details</h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">applies to all {sheet.returned} in this batch</span>
              </div>
              <CopyBtn text={defaultsText} label="All defaults" tid="copy-all-defaults" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div className="flex items-center justify-between gap-2 bg-[#C05A45]/5 border border-[#C05A45]/20 rounded-md p-2.5" data-testid="default-category">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-[#C05A45] font-semibold">Category (search this)</div>
                  <div className="text-sm text-zinc-800 truncate">{sheet.category_suggestion || "—"}</div>
                </div>
                <CopyBtn text={sheet.category_suggestion} label="Category" tid="copy-category" />
              </div>
              {/* Editable State Produced */}
              <div className="flex items-center justify-between gap-2 bg-[#C05A45]/5 border border-[#C05A45]/20 rounded-md p-2.5" data-testid="default-state-produced">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wide text-[#C05A45] font-semibold">State Produced (set once)</div>
                  <Input value={stateProduced} onChange={(e) => setStateProduced(e.target.value)} data-testid="state-produced-input"
                    placeholder="e.g. Texas" className="h-7 mt-0.5 text-sm bg-white" />
                </div>
                <CopyBtn text={stateProduced} label="State Produced" tid="copy-state-produced" />
              </div>
              {Object.entries(sheet.common_defaults || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-2 bg-zinc-50 rounded-md p-2.5" data-testid={`default-${k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">{k}</div>
                    <div className="text-sm text-zinc-800 truncate">{v}</div>
                  </div>
                  <CopyBtn text={v} label={k} tid={`copy-default-${k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3" data-testid="product-cards">
            {sheet.items.map((it, idx) => (
              <div key={it.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`product-card-${idx}`}>
                <div className="flex gap-4">
                  <img src={productImage(it)} alt="" className="w-16 h-16 rounded object-cover bg-zinc-100 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2.5">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-0.5">Original Shopify title (too long for RangeMe)</div>
                      <div className="text-xs text-zinc-400 break-words">{it.original_title}</div>
                    </div>
                    <div className="flex items-start justify-between gap-2 bg-[#C05A45]/5 border border-[#C05A45]/20 rounded-md p-2.5">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-[#C05A45] font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> RangeMe product name</div>
                        <div className="text-sm font-semibold text-zinc-800 break-words">{it.short_name}</div>
                      </div>
                      <CopyBtn text={it.short_name} label="Product name" tid={`copy-name-${idx}`} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">USPs</div>
                        <CopyBtn text={it.usps.join("\n")} label="All USPs" tid={`copy-usps-all-${idx}`} />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {it.usps.map((u, i) => (
                          <button key={i} onClick={() => copy(u, "USP")} data-testid={`usp-${idx}-${i}`}
                            className="text-xs text-zinc-700 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-full px-2.5 py-1 transition-colors flex items-center gap-1">
                            {u} <Copy className="w-3 h-3 text-zinc-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {sheet.items.length === 0 && <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-400">No products in this batch.</div>}
          </div>
        </>
      )}

      {/* ---- CHECKLIST VIEW ---- */}
      {view === "checklist" && checklist && (
        <div data-testid="checklist-view">
          <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4">
            <p className="text-xs text-zinc-500 max-w-3xl">
              RangeMe stores the images it imported — changing Shopify later doesn't refresh them, so any product
              imported <strong>before</strong> its gallery was cleaned still shows folded shots in RangeMe.
              <span className="text-emerald-600 font-medium"> "Gallery cleaned"</span> means Shopify is ready — safe to
              delete &amp; re-import into RangeMe. <span className="text-amber-600 font-medium">"Not cleaned"</span> means
              run the Distribution folded-image cleanup first (newer products may already be clean by design). Check off
              each one as you re-import — the tracker is saved on this device.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span className="text-zinc-600"><strong>{checklist.total}</strong> {TYPE_LABELS[checklist.product_type] || checklist.product_type} products</span>
              <span className="text-emerald-600"><strong>{checklist.cleaned}</strong> gallery cleaned</span>
              <span className="text-amber-600"><strong>{checklist.total - checklist.cleaned}</strong> not cleaned</span>
              <span className="text-[#C05A45]"><strong>{checklist.items.filter((i) => reimported.has(i.id)).length}</strong> marked re-imported</span>
            </div>
            {/* Migration progress bar */}
            {checklist.total > 0 && (() => {
              const done = checklist.items.filter((i) => reimported.has(i.id)).length;
              const pct = Math.round((done / checklist.total) * 100);
              return (
                <div className="mt-3" data-testid="migration-progress">
                  <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
                    <span>Re-import progress</span>
                    <span data-testid="migration-progress-label"><strong className="text-zinc-700">{done} of {checklist.total}</strong> re-imported · {pct}%</span>
                  </div>
                  <div className="h-2.5 w-full bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#C05A45] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="migration-progress-bar" />
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="space-y-2" data-testid="checklist-items">
            {checklist.items.map((it, idx) => (
              <div key={it.id} data-testid={`checklist-row-${idx}`}
                className={`bg-white border rounded-lg p-3 flex items-center gap-3 transition-colors ${reimported.has(it.id) ? "border-[#C05A45]/40 bg-[#C05A45]/[0.03]" : "border-zinc-200"}`}>
                <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                  <input type="checkbox" checked={reimported.has(it.id)} onChange={() => toggleReimported(it.id)}
                    data-testid={`reimported-${idx}`} className="accent-[#C05A45] w-4 h-4" />
                </label>
                <img src={productImage(it)} alt="" className="w-11 h-11 rounded object-cover bg-zinc-100 shrink-0" />
                <div className={`min-w-0 flex-1 ${reimported.has(it.id) ? "line-through text-zinc-400" : ""}`}>
                  <div className="text-sm font-medium truncate">{it.title}</div>
                </div>
                {it.gallery_cleaned ? (
                  <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0" data-testid={`status-clean-${idx}`}>
                    <CheckCircle2 className="w-3 h-3" /> Gallery cleaned
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0" data-testid={`status-dirty-${idx}`}>
                    <AlertTriangle className="w-3 h-3" /> Not cleaned
                  </span>
                )}
              </div>
            ))}
            {checklist.items.length === 0 && <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-400">No published products of this type.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
